import { useMemo, useState } from 'react';
import { fetchCourseContentList } from '@/services/course';
import type { CourseContentDto } from '@/services/types';
import { useRequest } from '@/hooks/useRequest';
import LessonPlayerModal, { type PlayingLesson } from './LessonPlayerModal';

/** 课时（url 为空表示不可播放：未解锁或暂无视频） */
type Lesson = { id: string; title: string; duration: string; url?: string };
type Chapter = { id: number; title: string; lessons: Lesson[] };

// mock 兜底目录（接口无数据时展示）
const mockChapters: Chapter[] = [
  {
    id: 1,
    title: '第一章 · 理论溯源与辨证基础',
    lessons: [
      { id: '1-1', title: '中西医结合诊疗的发展脉络与理论根基', duration: '18:24' },
      { id: '1-2', title: '阴阳五行与脏腑辨证的核心要点', duration: '22:10' },
      { id: '1-3', title: '四诊合参与现代检查的融合应用', duration: '25:36' },
    ],
  },
  {
    id: 2,
    title: '第二章 · 临床常见证型辨析',
    lessons: [
      { id: '2-1', title: '气血津液辨证的临床思路', duration: '19:48' },
      { id: '2-2', title: '常见内科证型的虚实辨别', duration: '23:15' },
    ],
  },
  {
    id: 3,
    title: '第三章 · 组方用药与加减思路',
    lessons: [
      { id: '3-1', title: '常用方剂的配伍规律解析', duration: '26:40' },
      { id: '3-2', title: '药对与引经药的灵活运用', duration: '20:33' },
      { id: '3-3', title: '随证加减的剂量把控技巧', duration: '24:18' },
    ],
  },
  {
    id: 4,
    title: '第四章 · 典型医案解析与实操',
    lessons: [
      { id: '4-1', title: '消渴病医案的辨证全过程', duration: '28:55' },
      { id: '4-2', title: '实操演示：针灸取穴与手法', duration: '27:26' },
    ],
  },
  {
    id: 5,
    title: '第五章 · 康复调养与疗效随访',
    lessons: [
      { id: '5-1', title: '疗程设定与疗效评估方法', duration: '16:50' },
      { id: '5-2', title: '饮食情志调养与随访管理', duration: '18:05' },
    ],
  },
];

/** 课时列表按章节分组 */
function buildChapters(list: CourseContentDto[]): Chapter[] {
  const map = new Map<number, Chapter>();
  list.forEach((item) => {
    const chapterId = item.chapter || 1;
    let chapter = map.get(chapterId);
    if (!chapter) {
      chapter = { id: chapterId, title: item.chapterStr || `第${chapterId}章`, lessons: [] };
      map.set(chapterId, chapter);
    }
    chapter.lessons.push({
      id: String(item.id),
      title: item.name || item.sectionStr || `第${item.section}节`,
      duration: item.durationStr || '',
      // 待购买课时后端不下发 url，无地址即不可播放（展示为锁定态）
      url: item.operationState === 3 ? '' : item.url || '',
    });
  });
  return Array.from(map.values()).sort((a, b) => a.id - b.id);
}

interface CourseOutlineProps {
  courseId?: number;
}

export default function CourseOutline({ courseId }: CourseOutlineProps) {
  const [openIds, setOpenIds] = useState<number[]>([1]);
  const [playing, setPlaying] = useState<PlayingLesson | null>(null);

  const { data } = useRequest(
    () =>
      courseId
        ? fetchCourseContentList(courseId).then((res) => res.list ?? [])
        : Promise.resolve([]),
    { fallback: [] },
  );

  const chapters = useMemo(() => {
    const remote = buildChapters(data);
    return remote.length > 0 ? remote : mockChapters;
  }, [data]);

  const toggle = (id: number) => {
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const totalLessons = chapters.reduce((sum, chapter) => sum + chapter.lessons.length, 0);

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
          <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
          课程目录
        </h2>
        <span className="text-xs text-foreground-500">
          共 {chapters.length} 章 · {totalLessons} 课时
        </span>
      </div>

      <div className="mt-4 rounded-lg border border-background-200 overflow-hidden divide-y divide-background-200">
        {chapters.map((chapter, index) => {
          const open = openIds.includes(chapter.id);
          return (
            <div key={chapter.id} className={open ? 'bg-background-100' : 'bg-background-50'}>
              <button
                type="button"
                onClick={() => toggle(chapter.id)}
                aria-expanded={open}
                className="w-full flex items-center gap-4 px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
              >
                <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-semibold">
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-foreground-800">{chapter.title}</span>
                <span className="ml-auto flex items-center gap-3 shrink-0">
                  <span className="hidden sm:inline text-xs text-foreground-500">{chapter.lessons.length} 课时</span>
                  <i
                    className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 ${
                      open ? 'rotate-180' : ''
                    }`}
                  ></i>
                </span>
              </button>

              {open && (
                <ul className="px-5 pb-3 space-y-1.5">
                  {chapter.lessons.map((lesson) =>
                    lesson.url ? (
                      <li key={lesson.id}>
                        <button
                          type="button"
                          onClick={() =>
                            setPlaying({ id: lesson.id, title: lesson.title, url: lesson.url || '' })
                          }
                          aria-label={`播放课时：${lesson.title}`}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer group/lesson"
                        >
                          <i className="ri-play-circle-line text-primary-500 text-base group-hover/lesson:text-primary-600"></i>
                          <span className="text-xs md:text-sm text-foreground-700 group-hover/lesson:text-foreground-900">
                            {lesson.title}
                          </span>
                          <span className="ml-auto flex items-center gap-3 shrink-0">
                            {lesson.duration && (
                              <span className="inline-flex items-center gap-1 text-xs text-foreground-500 whitespace-nowrap">
                                <i className="ri-time-line"></i>
                                {lesson.duration}
                              </span>
                            )}
                            <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-primary-600 whitespace-nowrap">
                              <i className="ri-play-fill"></i>
                              立即观看
                            </span>
                          </span>
                        </button>
                      </li>
                    ) : (
                      <li
                        key={lesson.id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-background-100 border border-background-200"
                      >
                        <i className="ri-lock-line text-foreground-400 text-base"></i>
                        <span className="text-xs md:text-sm text-foreground-500">{lesson.title}</span>
                        <span className="ml-auto flex items-center gap-3 shrink-0">
                          {lesson.duration && (
                            <span className="inline-flex items-center gap-1 text-xs text-foreground-400 whitespace-nowrap">
                              <i className="ri-time-line"></i>
                              {lesson.duration}
                            </span>
                          )}
                          <span className="hidden sm:inline text-xs text-foreground-400 whitespace-nowrap">
                            购买后可观看
                          </span>
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <LessonPlayerModal open={playing !== null} lesson={playing} onClose={() => setPlaying(null)} />
    </div>
  );
}